import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TablePaginationBar } from '../../../components/ui/TablePaginationBar';
import { TableTemplate } from '../../../components/ui/tableTemplate/TableTemplate';
import type { ListFilterValue } from '../../../components/ui/tableTemplate/TableTemplateTypes';
import { useTableState } from '../../../hooks/useTableState';
import theme from '../../../theme/theme';
import { EventInboxStatsBar } from '../components/EventInboxStatsBar';
import { useEventInboxColumns } from '../hooks/useEventInboxColumns';
import { useGetEventInbox } from '../hooks/useGetEventInbox';
import { useGetEventInboxStats } from '../hooks/useGetEventInboxStats';
import type { EventInboxListItem, EventInboxStatus } from '../eventInboxTypes';

/** Read-only Event Inbox list — KPI bar + inline column filters + paginated table. Route: `/event-inbox`. */
export function EventInboxListView() {
  const { t } = useTranslation(['eventInbox', 'common']);
  const navigate = useNavigate();

  const { filters, debouncedFilters, setFilter, clearFilter, page, limit, setPage, setLimit } =
    useTableState<EventInboxListItem>();

  const columns = useEventInboxColumns();
  const { data, meta, loading, error, refetch } = useGetEventInbox(
    debouncedFilters,
    columns,
    page,
    limit,
  );

  // The KPI stats bar offers a single-select status shortcut on top of the shared
  // `status` list filter — the same filter the status column header drives, so both
  // stay in sync through FilterState.
  const activeStatus = (filters.status as ListFilterValue | undefined)?.values[0] as
    | EventInboxStatus
    | undefined;
  const toggleStatus = useCallback(
    (status: EventInboxStatus) => {
      if (activeStatus === status) clearFilter('status');
      else setFilter('status', { kind: 'list', values: [status], op: 'in' });
    },
    [activeStatus, clearFilter, setFilter],
  );

  const { data: stats, loading: statsLoading, refetch: refetchStats } = useGetEventInboxStats();

  const handleRefresh = useCallback(() => {
    refetch();
    refetchStats();
  }, [refetch, refetchStats]);

  const paginationMeta = meta ?? {
    total: data.length,
    page,
    limit,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  };

  return (
    <Box
      sx={{
        px: 3,
        pt: 3,
        pb: 4,
        bgcolor: theme.palette.grey[50],
        minHeight: `calc(100vh - ${theme.toolbar.height}px)`,
        overflowY: 'auto',
      }}
    >
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            // No matching theme token for 46px / this shadow — kept as-is (opted
            // not to extend the theme). Radius maps to the shared scale.
            width: 46,
            height: 46,
            borderRadius: theme.borderRadius.lg,
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            flexShrink: 0,
          }}
        >
          <ReceiptLongIcon sx={{ color: theme.palette.common.white, fontSize: theme.iconSize.md }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h5"
            fontWeight={theme.typography.fontWeightBold}
            color="primary.main"
            sx={{ lineHeight: 1.2 }}
          >
            {t('eventInbox:pageTitle')}
          </Typography>
          <Typography variant="body2" color="text.label">
            {t('eventInbox:pageSubtitle')}
          </Typography>
        </Box>

        <Tooltip title={t('eventInbox:actions.refresh')}>
          <span>
            <IconButton
              onClick={handleRefresh}
              disabled={loading || statsLoading}
              aria-label={t('eventInbox:actions.refresh')}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: theme.borderRadius.sm,
                bgcolor: 'background.paper',
              }}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* KPI bar */}
      <EventInboxStatsBar
        stats={stats}
        loading={statsLoading}
        activeStatus={activeStatus}
        onSelectStatus={toggleStatus}
        onClearStatus={() => clearFilter('status')}
      />

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('eventInbox:errors.loadError')}
        </Alert>
      )}

      <TableTemplate<EventInboxListItem>
        columns={columns}
        rows={data}
        loading={loading}
        filterState={filters}
        onFilterChange={setFilter}
        onFilterClear={clearFilter}
        toolbar={
          <Typography variant="body2" color="text.label">
            <b>{paginationMeta.total}</b> {t('eventInbox:count', { count: paginationMeta.total })}
          </Typography>
        }
        onRowClick={row => navigate(`/event-inbox/${row.id}`)}
        pagination={
          <TablePaginationBar
            meta={paginationMeta}
            page={page}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        }
      />
    </Box>
  );
}
